import React, { useState } from 'react';
import { Upload, X, Send, Loader2, Phone, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useRecaptcha } from '../hooks/useRecaptcha';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HTTP_FORBIDDEN = 403;

export const SimpleLanding = () => {
  const { executeRecaptcha, isReady } = useRecaptcha();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    dirtLevel: 5
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let recaptchaToken = null;
      
      // Try to get reCAPTCHA token, but don't fail if it's not available
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('quote_submission');
        } catch (error) {
          console.warn('[recaptcha] token unavailable, submitting without it', error);
        }
      }
      
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('email', 'noreply@customer.com'); // Required field
      submitData.append('service', 'quick-quote');
      submitData.append('message', `Car Dirt Level: ${formData.dirtLevel}/10`);
      submitData.append('requestCallback', true);
      
      if (recaptchaToken) {
        submitData.append('recaptchaToken', recaptchaToken);
      }

      files.forEach((file) => {
        submitData.append('files', file);
      });

      await axios.post(`${BACKEND_URL}/api/quotes`, submitData);

      toast.success('Request submitted! We\'ll contact you shortly.');

      setFormData({
        name: '',
        phone: '',
        dirtLevel: 5
      });
      setFiles([]);

      const fileInput = document.getElementById('photo-upload');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      if (error.response?.status === HTTP_FORBIDDEN) {
        toast.error('Security verification failed. Please try again.');
      } else {
        toast.error('Failed to submit. Please call (505) 595-9416 directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-neutral-950 to-black flex items-center justify-center p-4">
      {/* Logo/Header */}
      <div className="absolute top-6 left-6">
        <img 
          src="/images/logo.png"
          alt="Stefen's Detailing"
          className="h-16 w-auto"
        />
      </div>

      {/* Phone Number - Top Right */}
      <a 
        href="tel:(505) 595-9416"
        className="absolute top-6 right-6 flex items-center space-x-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-sm transition-colors duration-200"
      >
        <Phone className="w-4 h-4 text-white" />
        <span className="text-white font-semibold">(505) 595-9416</span>
      </a>

      {/* Main Content */}
      <div className="max-w-2xl w-full">
        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
            Get Your Quote in<br />
            <span className="text-red-600">60 Seconds</span>
          </h1>
          <p className="text-xl text-white/80">
            Premium Mobile Detailing • Albuquerque & Los Lunas
          </p>
          
          {/* Trust Badge */}
          <div className="flex items-center justify-center space-x-2 mt-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={`star-${i}`} className="w-5 h-5 fill-red-600 text-red-600" />
              ))}
            </div>
            <span className="text-white/70">5-Star Rated Service</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-neutral-900/50 backdrop-blur-lg border border-white/10 rounded-lg p-8 md:p-12 shadow-2xl">
          {/* Name */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-white font-semibold mb-2 text-lg">
              Your Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-4 bg-black/50 border border-white/20 rounded-sm text-white text-lg placeholder-white/40 focus:outline-none focus:border-red-600 transition-colors duration-200"
              placeholder="John Doe"
            />
          </div>

          {/* Phone */}
          <div className="mb-6">
            <label htmlFor="phone" className="block text-white font-semibold mb-2 text-lg">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-4 bg-black/50 border border-white/20 rounded-sm text-white text-lg placeholder-white/40 focus:outline-none focus:border-red-600 transition-colors duration-200"
              placeholder="(505) 555-0123"
            />
          </div>

          {/* Dirt Level Slider */}
          <div className="mb-6">
            <label htmlFor="dirtLevel" className="block text-white font-semibold mb-3 text-lg">
              How dirty is your car? 
              <span className="ml-3 text-red-600 text-2xl font-bold">{formData.dirtLevel}/10</span>
            </label>
            <input
              type="range"
              id="dirtLevel"
              name="dirtLevel"
              min="1"
              max="10"
              value={formData.dirtLevel}
              onChange={handleInputChange}
              className="w-full h-3 bg-black/50 rounded-lg appearance-none cursor-pointer accent-red-600"
              style={{
                background: `linear-gradient(to right, #C1121F 0%, #C1121F ${(formData.dirtLevel - 1) * 11.11}%, rgba(255,255,255,0.2) ${(formData.dirtLevel - 1) * 11.11}%, rgba(255,255,255,0.2) 100%)`
              }}
            />
            <div className="flex justify-between mt-2 text-white/60 text-sm">
              <span>Slightly Dusty</span>
              <span>Needs Deep Clean</span>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="mb-8">
            <label className="block text-white font-semibold mb-3 text-lg">
              Upload Photos of Your Car
            </label>
            <div className="relative">
              <input
                type="file"
                id="photo-upload"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="photo-upload"
                className="flex items-center justify-center w-full px-4 py-10 bg-black/50 border-2 border-dashed border-white/20 rounded-sm cursor-pointer hover:border-red-600 transition-colors duration-200"
              >
                <div className="text-center">
                  <Upload className="w-10 h-10 text-red-600 mx-auto mb-3" />
                  <span className="text-white/80 text-lg">Click to upload photos</span>
                  <p className="text-white/40 text-sm mt-2">Help us give you an accurate quote</p>
                </div>
              </label>
            </div>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between bg-black/30 px-4 py-3 rounded-sm">
                    <span className="text-white/80 truncate flex-1">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="ml-3 text-red-600 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full group px-8 py-5 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white text-xl font-bold rounded-sm transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg hover:shadow-red-600/50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <span>Get My Quote</span>
                <Send className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>

          <p className="text-center text-white/50 text-sm mt-4">
            We&apos;ll contact you within minutes to discuss your quote
          </p>
          
          <p className="text-center text-white/40 text-xs mt-3">
            This site is protected by reCAPTCHA and the Google{' '}
            <a 
              href="https://policies.google.com/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-white/60"
            >
              Privacy Policy
            </a>
            {' '}and{' '}
            <a 
              href="https://policies.google.com/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-white/60"
            >
              Terms of Service
            </a>
            {' '}apply.
          </p>
        </form>

        {/* Footer Info */}
        <div className="text-center mt-8 text-white/60">
          <p>Fully Mobile • Professional Grade Equipment • Attention to Detail</p>
        </div>
      </div>
    </div>
  );
};
